/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Globe, KeyRound, Plus, X } from "lucide-react";
import { CopyableField, RadioGroup, SelectDropdown, StatusBadge, TextInput, confirmDelete, showErrorAlert } from "../../../../components/ui/index.js";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import { apiErrorText, formatDate } from "../../../../components/crm/crmUtils.js";

const STATUS = {
    active: { label: "Activo", tone: "success" },
    expired: { label: "Caducado", tone: "neutral" },
    revoked: { label: "Revocado", tone: "error" },
};

const EXPIRY_OPTIONS = [
    { value: "30", label: "30 días" },
    { value: "90", label: "90 días" },
    { value: "180", label: "6 meses" },
    { value: "365", label: "1 año" },
];

const IPS_HELP = "Una IP o red por línea o separadas por comas (p. ej. 203.0.113.7 o 100.64.0.0/10 para Tailscale). Vacío: desde cualquier sitio.";

const ipsText = (ips) => (ips || []).join("\n");

/** Cambiar desde qué IPs vale un token (C3D-51). */
function AllowedIpsModal({ token, onClose, onSaved }) {
    const [value, setValue] = useState(ipsText(token.allowed_ips));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const addLastUsed = () => {
        const current = value.split(/[\n,]/).map((part) => part.trim()).filter(Boolean);
        if (token.last_used_ip && !current.includes(token.last_used_ip)) {
            setValue([...current, token.last_used_ip].join("\n"));
        }
    };

    const save = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            await axios.put(`/api-tokens/${token.id}/allowed-ips/`, { allowed_ips: value });
            onSaved();
            onClose();
        } catch (err) {
            setError(apiErrorText(err, "No se han podido guardar las IPs."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="token-ips-title">
            <form className="modal-box flex flex-col gap-4" onSubmit={save}>
                <div className="flex items-center justify-between">
                    <h2 id="token-ips-title" className="text-lg font-semibold">Desde dónde vale «{token.name}»</h2>
                    <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Cerrar">
                        <X className="w-5 h-5" aria-hidden="true" />
                    </button>
                </div>
                <label className="form-control gap-1">
                    <span className="label-text text-sm font-medium">IPs permitidas</span>
                    <textarea className="textarea textarea-bordered textarea-sm font-mono" rows={4} value={value} onChange={(e) => setValue(e.target.value)} />
                    <span className="text-xs text-base-content/60">{IPS_HELP}</span>
                </label>
                {token.last_used_ip && (
                    <button type="button" className="btn btn-ghost btn-xs self-start" onClick={addLastUsed}>
                        Añadir la del último uso ({token.last_used_ip})
                    </button>
                )}
                {error && <p role="alert" className="text-sm text-error">{error}</p>}
                <div className="modal-action mt-0">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                        {saving && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                        Guardar
                    </button>
                </div>
            </form>
            <button type="button" className="modal-backdrop" onClick={onClose} aria-label="Cerrar" />
        </div>
    );
}

/** Alta de un token. Al crearlo enseña su valor una única vez. */
function NewTokenModal({ scopes, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [scope, setScope] = useState(scopes[0]?.value || "crm");
    const [days, setDays] = useState("90");
    const [ips, setIps] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [created, setCreated] = useState(null);

    const create = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            const { data } = await axios.post("/api-tokens/", { name, scopes: [scope], expires_in_days: Number(days), allowed_ips: ips });
            setCreated(data);
            onCreated();
        } catch (err) {
            setError(apiErrorText(err, "No se ha podido crear el token."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="new-token-title">
            <div className="modal-box flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h2 id="new-token-title" className="text-lg font-semibold">{created ? "Token creado" : "Nuevo token"}</h2>
                    <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Cerrar">
                        <X className="w-5 h-5" aria-hidden="true" />
                    </button>
                </div>

                {created ? (
                    <>
                        <div role="alert" className="alert alert-warning text-sm items-start">
                            <AlertTriangle className="w-5 h-5 shrink-0" aria-hidden="true" />
                            <span>Cópialo ahora y guárdalo en la configuración del MCP. Por seguridad no se vuelve a enseñar: si lo pierdes, revócalo y crea otro.</span>
                        </div>
                        <CopyableField label={created.name} value={created.token} helpText={`Caduca el ${formatDate(created.expires_at)}`} />
                        <div className="modal-action mt-0">
                            <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>Ya lo he copiado</button>
                        </div>
                    </>
                ) : (
                    <form className="flex flex-col gap-4" onSubmit={create}>
                        <label className="form-control gap-1">
                            <span className="label-text text-sm font-medium">Nombre</span>
                            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="MCP del portátil" required autoFocus maxLength={80} />
                            <span className="text-xs text-base-content/60">Para saber dónde lo usas y cuál revocar.</span>
                        </label>
                        <div className="flex flex-col gap-2">
                            <span className="label-text text-sm font-medium">Qué puede hacer</span>
                            <RadioGroup name="token-scope" options={scopes} value={scope} onChange={setScope} direction="vertical" />
                        </div>
                        <div className="form-control gap-1">
                            <span className="label-text text-sm font-medium">Caduca en</span>
                            <SelectDropdown options={EXPIRY_OPTIONS} value={days} onChange={setDays} className="w-48" />
                        </div>
                        <label className="form-control gap-1">
                            <span className="label-text text-sm font-medium">Desde qué IPs (opcional)</span>
                            <textarea className="textarea textarea-bordered textarea-sm font-mono" rows={2} value={ips} onChange={(e) => setIps(e.target.value)} />
                            <span className="text-xs text-base-content/60">{IPS_HELP}</span>
                        </label>
                        <p className="text-xs text-base-content/60">
                            Puede lo mismo que tu rol, nunca más. Un token no puede borrar, activar enlaces de catálogo, convertir leads ni gestionar tokens.
                        </p>
                        {error && <p role="alert" className="text-sm text-error">{error}</p>}
                        <div className="modal-action mt-0">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-primary btn-sm" disabled={!name.trim() || saving}>
                                {saving && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                                Crear token
                            </button>
                        </div>
                    </form>
                )}
            </div>
            <button type="button" className="modal-backdrop" onClick={onClose} aria-label="Cerrar" />
        </div>
    );
}

/**
 * Admin › CRM › Tokens de API (C3D-41): los tokens personales con los que el
 * MCP de prospección habla con la API en tu nombre.
 */
export default function AdminApiTokens() {
    const isAdmin = useAuthContext().role === "admin";
    const [showAll, setShowAll] = useState(false);
    const [state, setState] = useState({ loading: true, error: false, data: null });
    const [creating, setCreating] = useState(false);
    const [editingIps, setEditingIps] = useState(null);

    const load = useCallback(() => {
        setState((prev) => ({ ...prev, loading: true, error: false }));
        axios.get("/api-tokens/", { params: showAll ? { all: 1 } : undefined })
            .then(({ data }) => setState({ loading: false, error: false, data }))
            .catch(() => setState({ loading: false, error: true, data: null }));
    }, [showAll]);
    useEffect(() => { load(); }, [load]);

    const revoke = async (token) => {
        const confirmed = await confirmDelete({
            title: "Revocar token",
            text: `"${token.name}" deja de funcionar al momento. Lo que lo use tendrá que configurarse con uno nuevo.`,
            confirmButtonText: "Revocar",
        });
        if (!confirmed) return;
        try {
            await axios.post(`/api-tokens/${token.id}/revoke/`);
            load();
        } catch (error) {
            showErrorAlert({ text: apiErrorText(error, "No se ha podido revocar el token.") });
        }
    };

    const tokens = state.data?.results || [];

    return (
        <div className="p-2 sm:p-4 flex flex-col gap-4">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold">Tokens de API</h1>
                    <p className="text-sm text-base-content/70 max-w-[65ch]">
                        Para que el MCP de prospección trabaje en tu nombre sin tu contraseña. Cada token caduca y se puede revocar.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <label className="label cursor-pointer gap-2">
                            <input type="checkbox" className="checkbox checkbox-sm" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                            <span className="label-text text-sm">Ver los de todos</span>
                        </label>
                    )}
                    {state.data?.can_create && (
                        <button type="button" className="btn btn-primary btn-sm gap-2" onClick={() => setCreating(true)}>
                            <Plus className="w-4 h-4" aria-hidden="true" /> Nuevo token
                        </button>
                    )}
                </div>
            </header>

            {state.error && (
                <div role="alert" className="alert alert-error justify-between">
                    <span>No se han podido cargar los tokens.</span>
                    <button type="button" className="btn btn-sm" onClick={load}>Reintentar</button>
                </div>
            )}

            {state.loading && !state.data && <div className="skeleton h-40 rounded-xl" aria-busy="true" />}

            {state.data && tokens.length === 0 && (
                <div className="flex flex-col items-center text-center gap-3 py-16 rounded-xl border border-dashed border-base-content/20">
                    <KeyRound className="w-10 h-10 text-base-content/40" aria-hidden="true" />
                    <p className="font-semibold">Todavía no hay tokens</p>
                    <p className="text-sm text-base-content/70 max-w-sm">Crea uno para conectar el MCP de prospección.</p>
                </div>
            )}

            {tokens.length > 0 && (
                <ul className="flex flex-col gap-3">
                    {tokens.map((token) => {
                        const status = STATUS[token.status] || STATUS.expired;
                        return (
                            <li key={token.id} className="rounded-xl border border-base-content/10 bg-base-100 p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                <div className="flex flex-col gap-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold">{token.name}</span>
                                        <StatusBadge label={status.label} tone={status.tone} size="xs" />
                                        <code className="text-xs text-base-content/60">{token.display}</code>
                                    </div>
                                    <p className="text-xs text-base-content/70">{token.scope_labels.join(", ")}</p>
                                    <p className="text-xs text-base-content/70 flex items-center gap-1">
                                        <Globe className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                                        {token.allowed_ips?.length ? `Solo desde ${token.allowed_ips.join(", ")}` : "Desde cualquier IP"}
                                    </p>
                                    <p className="text-xs text-base-content/60">
                                        {showAll ? `${token.user_email}. ` : ""}
                                        Creado el {formatDate(token.created_at)}. {token.status === "revoked" ? `Revocado el ${formatDate(token.revoked_at)}.` : `Caduca el ${formatDate(token.expires_at)}.`}
                                        {" "}{token.last_used_at ? `Último uso el ${formatDate(token.last_used_at)}${token.last_used_ip ? ` desde ${token.last_used_ip}` : ""}.` : "Sin usar todavía."}
                                    </p>
                                </div>
                                {token.status === "active" && (
                                    <div className="flex gap-1 self-start md:self-center">
                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditingIps(token)}>
                                            IPs
                                        </button>
                                        <button type="button" className="btn btn-ghost btn-sm text-error" onClick={() => revoke(token)}>
                                            Revocar
                                        </button>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {editingIps && (
                <AllowedIpsModal token={editingIps} onClose={() => setEditingIps(null)} onSaved={load} />
            )}

            {creating && state.data && (
                <NewTokenModal scopes={state.data.scopes} onClose={() => setCreating(false)} onCreated={load} />
            )}
        </div>
    );
}
